<template>
  <div v-if="showTable && (hasRows || collapsed)" class="summary-table-container m-0">
    <FieldPicker v-model="fieldSelected" :show="showFieldPicker" :options="summaryFields" />

    <div class="d-flex justify-content-between align-items-start">
      <TableFormatter
        v-if="hasRows"
        :items="items"
        :label-name="labelName"
        :label-value="labelValue"
        :display="display"
        :is-root="isRootOrSubroot"
        :show-footer="showFooter"
        :child-total="childTotal"
      />
      <ExpandTableButton
        v-model="collapsed"
        :show="showExpandButton"
        :show-collapse-button-text="collapsed && !hasRows"
        :collapse-button-text="collapseButtonText"
      />
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import FieldPicker from '@/components/public/SummaryTable/FieldPicker.vue'
import ExpandTableButton from '@/components/public/SummaryTable/ExpandTableButton.vue'
import TableFormatter from '@/components/public/SummaryTable/TableFormatter.vue'
import { Path, SummaryTableOptions, TreeNode } from '@/types'
import { escapeRegExp } from 'lodash'

const components = {
  FieldPicker,
  ExpandTableButton,
  TableFormatter,
}

@Component({ components })
export default class SummaryTable extends Vue {
  fieldSelected = ''
  collapsed = false
  showCollapseButtonText = false

  mounted() {
    if (this.startCollapsed) {
      this.collapsed = true
    }
  }

  get app() {
    return this.$typedStore.activeVisualisation.visualisationStore.app
  }

  get config() {
    return this.app.config
  }

  get summaryTable() {
    return this.config.summaryTable
  }

  get summaryColumns() {
    return this.config.summaryItems
  }

  get collapsable() {
    return this.config.summaryTable.collapsable
  }

  get collapseButtonText() {
    return this.config.summaryTable.collapseButtonText
  }

  get displayAutoTotal() {
    return this.config.summaryTable.displayAutoTotal
  }

  get startCollapsed() {
    return this.config.summaryTable.startCollapsed
  }

  get totalStringLabel() {
    return this.config.appConfig.totalStringLabel
  }

  get projectLabelField() {
    return this.config.appConfig.projectLabelField
  }

  get sortTabOrder() {
    return this.config.summaryTable.sortTabOrder
  }

  get filteredLeafDataByCurrentPath() {
    return this.$typedStore.visualisationData.filteredLeafDataByCurrentPath
  }

  get levels() {
    return this.app.levels
  }

  get sortedRootChildren() {
    return this.$typedStore.visualisationData.sortedRootChildren
  }

  get fieldMap() {
    return this.$typedStore.visualisationApp.fieldMap
  }

  get storyPath() {
    return this.$typedStore.activeVisualisation.storyPath
  }

  get showTable() {
    return (
      this.enabled && this.summaryFields.length > 0 && this.display !== SummaryTableOptions.NONE
    )
  }

  get level() {
    return this.storyPath.level
  }

  get isRootOrSubroot() {
    return this.storyPath.isRootOrSubroot
  }

  get display() {
    if (this.storyPath.isRoot) {
      return this.summaryTable.root
    }

    if (this.storyPath.isSubroot) {
      return this.summaryTable.subroot
    }

    return this.summaryTable.levelOptions[this.level]
  }

  get summaryFields() {
    return this.summaryColumns.map((c) => this.fieldMap.find(({ column }) => c === column))
  }

  get hasRows() {
    return this.items.length > 0 && this.summaryColumns.length > 0
  }

  get showFieldPicker() {
    return this.hasRows && this.display === SummaryTableOptions.CHILD
  }

  get childItems() {
    if (this.sortTabOrder && this.isRootOrSubroot) {
      return this.rootChildItems
    } else {
      return this.nonRootChildItems
    }
  }

  get nonRootChildItems() {
    return this.children.map((item) => {
      if (!item.children?.length) {
        const node = this.$typedStore.visualisationData.treeRoot.search(item.path)
        if (node) return this.mapChildItem(item, node.data?.[this.projectLabelField] || item.name)
      }
      return this.mapChildItem(item, item.name)
    })
  }

  get rootChildItems() {
    return this.sortedRootChildren.map((item) => {
      const name = this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.name(
        item.path,
      )
      const label = this.$typedStore.activeVisualisation.visualisationStore.entities.nodes.label(
        item.path,
      )
      return this.mapChildItem(item, label || name)
    })
  }

  get childTotal() {
    return {
      isTotal: true,
      name: this.totalStringLabel,
      value: this.$typedStore.visualisationData.fieldValueForColumn(this.fieldSelected),
      column: this.fieldSelected,
      _rowVariant: 'bold',
    }
  }

  get summaryItems() {
    return this.summaryColumns
      .map((column) => {
        const field = this.$typedStore.visualisationApp.field(column)
        return {
          field,
          name: field && field.name,
          value: this.$typedStore.visualisationData.fieldValueForColumn(column),
          column,
        }
      })
      .filter((i) => i.field) // filter out items that did not return fields
  }

  get items() {
    if (this.display === SummaryTableOptions.CHILD) {
      const showTotal = this.displayAutoTotal ? [this.childTotal] : []
      if (this.showFirstRowOnly) {
        return showTotal
      } else {
        return this.childItems
      }
    } else if (this.display === SummaryTableOptions.SUMMARY) {
      return this.showFirstRowOnly ? this.summaryItems.slice(0, 1) : this.summaryItems
    } else {
      return []
    }
  }

  get showFirstRowOnly() {
    return this.collapsable && this.collapsed
  }

  get children() {
    return this.$typedStore.visualisationData.childrenByPath(
      this.storyPath.isSubroot ? Path.root : this.storyPath,
    )
  }

  get enabled() {
    return this.config.appConfig.showSummaryTable
  }

  get labelValue() {
    const field = this.$typedStore.visualisationApp.field(this.fieldSelected)
    return field && field.name
  }

  get labelName() {
    if (this.display !== SummaryTableOptions.CHILD) {
      return ''
    }
    let column
    if (this.isRootOrSubroot) {
      column = this.levels.columnForLevel(1)
    } else if (this.level === 2) {
      column = this.projectLabelField
    } else {
      column = this.levels.columnForLevel(this.level + 1)
    }
    const field = this.$typedStore.visualisationApp.field(column)
    return field ? field.name : ''
  }

  get showExpandButton() {
    if (this.display === SummaryTableOptions.SUMMARY) {
      return this.collapsable && this.summaryColumns.length > 1
    } else {
      return this.collapsable
    }
  }

  get showFooter() {
    return (
      this.display === SummaryTableOptions.CHILD && this.displayAutoTotal && !this.showFirstRowOnly
    )
  }

  valueChild(path: string) {
    const data = this.filteredLeafDataByCurrentPath.filter((item) =>
      item.path.toString().match(`^${escapeRegExp(path)}(/|$)`),
    )
    return this.$typedStore.visualisationData.fieldValueForColumn(this.fieldSelected, data)
  }

  mapChildItem(item: TreeNode, name: string) {
    return {
      name,
      value: this.valueChild(item.path.toString()),
      column: this.fieldSelected,
      path: item.path.toString(),
    }
  }
}
</script>
